import { isEqual } from 'lodash';
import { DataTexture, ShaderMaterial } from 'three';
import {
  defaultPointSize,
  PointColorMode,
} from '../../../cross-team-shared/state/layer-settings-state-3d';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { H3Material } from '../basic/h3-basic-material';
import { H3Defines, H3SharedUniforms } from '../shared/definitions';
import { CloudColorUniforms } from './shaders/color/get-color';
import { setHeightModeUniforms } from './shaders/color/modes/height';
import { setInsideCuboidModeUniforms } from './shaders/color/modes/inside-cuboid';
import { setIntensityModeUniforms } from './shaders/color/modes/intensity';
import { setRegionOfInterestUniforms } from './shaders/color/modes/region-of-interest';
import { setSensorModeUniforms } from './shaders/color/modes/sensor';
import { setViewBoxHighlightUniforms } from './shaders/color/modes/viewbox-highlight';
import { mainCloudVertexShader } from './shaders/main-shader';
import { setSubpointMeasurementModeUniforms } from './shaders/subpoint-measurement-mode';

// Textures can not be declared inside nested uniforms (structs)
export interface CloudShaderUniforms extends H3SharedUniforms {
  color: CloudColorUniforms;
  // for sensor mode
  pointColorBySensor: DataTexture | undefined;
  useSubPointMeasurementMode: boolean;
  hideGround: boolean;
  pointSize: number;
  compensatedCuboids: DataTexture;
  compensatedCuboidCount: number;
}

export class CloudShaderMaterial
  extends ShaderMaterial
  implements H3Material<CloudShaderUniforms>
{
  override defines: H3Defines = {
    USE_LUT_PROJECTION: false,
    USE_CYLINDRICAL_PROJECTION: false,
    USE_DEFORMED_CYLINDRICAL_PROJECTION: false,
    USE_FISHEYE_PROJECTION: false,
    USE_MOTION_COMPENSATION: false,
    USE_BROWN_CONRADY_PROJECTION: false,
    IS_POINT: true,
    IS_CUBOID: false,
  };

  constructor() {
    super();

    this.precision = 'highp';
    this.vertexShader = mainCloudVertexShader;
    this.fragmentShader = fragmentShader;

    this.uniforms.color = { value: {} };
    setDefaultCloudMaterialUniforms(this);
  }

  setUniform<K extends keyof CloudShaderUniforms>(
    key: K,
    value: CloudShaderUniforms[K],
  ) {
    this.uniforms[key as string] = { value };
    this.uniformsNeedUpdate = true;
  }

  setColorUniform<K extends keyof CloudColorUniforms>(
    key: K,
    value: CloudColorUniforms[K],
  ) {
    this.uniforms.color.value[key as string] = value;
    this.uniformsNeedUpdate = true;
  }

  assignDefines<K extends keyof H3Defines>(defines: Pick<H3Defines, K>) {
    const newDefines: H3Defines = { ...this.defines, ...defines };

    // Prevents triggering a shader update which requires a program compilation
    if (isEqual(this.defines, newDefines)) {
      return;
    }

    this.defines = newDefines;
    this.needsUpdate = true;
  }
}

export function setUniformsForColorMode(
  mode: PointColorMode,
  state: PointCloudContentState,
  material: CloudShaderMaterial,
) {
  material.setColorUniform('mode', mode);
  setInsideCuboidModeUniforms(material, state);

  switch (mode) {
    case PointColorMode.Intensity:
      return setIntensityModeUniforms(material, state);
    case PointColorMode.Height:
      return setHeightModeUniforms(material, state);
    case PointColorMode.Sensor:
      return setSensorModeUniforms(material, state);
  }
}

export function setDefaultCloudMaterialUniforms(material: CloudShaderMaterial) {
  setDefaultSharedUniforms(material);
  setViewBoxHighlightUniforms(material);
  setRegionOfInterestUniforms(material);
  setSubpointMeasurementModeUniforms(material);
  setSensorModeUniforms(material);
  setIntensityModeUniforms(material);
  setInsideCuboidModeUniforms(material);
  setHeightModeUniforms(material);
}

function setDefaultSharedUniforms(material: CloudShaderMaterial) {
  material.setColorUniform('mode', PointColorMode.Intensity);
  material.setUniform('useSubPointMeasurementMode', false);
  material.setUniform('hideGround', false);
  material.setUniform('pointSize', defaultPointSize);
  material.setUniform('focalLength', [0, 0]);
  material.setUniform('principlePoint', [0, 0]);
  material.setUniform('imageSize', [0, 0]);
}

const fragmentShader = `
  varying vec4 vColor;

  void main() {
    gl_FragColor = vColor;
  }
`;
